import {useCallback, useMemo, useRef, useState} from 'react'
import {Comments} from '../comments/Comments'
import {CommentsContext, ViewMode} from '../comments/CommentsContext'
import {CommentsViewControls} from '../comments/CommentsViewControls'
import {
  IssueCommentComposer,
  useIsIssueCommentComposerEnabled,
  type IssueCommentComposerHandle,
} from '../comments/IssueCommentComposer'
import {useCommentsData} from '../comments/use-comments-data'
import {useCommentsFilters} from '../comments/use-comments-filters'
import {useLayoutAuthorLogin} from '../../stores/layout-selectors'
import {usePullRequestBasePath} from '../../stores/selectors'
import styles from './CommentsSection.module.css'
import {Stack} from '@primer/react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'

export function CommentsSection() {
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.IssueComments)

  const commentsContextValue = useMemo(() => ({viewMode, setViewMode}), [viewMode, setViewMode])

  return (
    <section>
      <h2 className={styles.heading}>Comments</h2>
      <ErrorBoundary
        // TODO: Improve error fallback
        fallback="Failed to load comments. Please refresh the page to try again."
        boundaryName="OverviewCommentsSection"
      >
        <CommentsContext value={commentsContextValue}>
          <CommentsSectionContent />
        </CommentsContext>
      </ErrorBoundary>
    </section>
  )
}

function CommentsSectionContent() {
  const pathName = usePullRequestBasePath()
  const pullRequestAuthorLogin = useLayoutAuthorLogin()

  const {
    threadPreviews,
    totalDiffCommentCount,
    conversationItems,
    mergedConversationComments,
    threadPreviewsAuthorList,
    issueCommentsAuthorList,
  } = useCommentsData()

  const {
    threadPreviewFilterState,
    setThreadPreviewFilterState,
    issueCommentsFilterState,
    setIssueCommentsFilterState,
    filteredThreadIds,
    filteredIssueCommentIds,
    selectedReviewId,
  } = useCommentsFilters({threadPreviews, conversationComments: mergedConversationComments})

  const composerRef = useRef<IssueCommentComposerHandle>(null)
  const handleQuoteReply = useCallback((quotedText: string) => composerRef.current?.insertQuote(quotedText), [])

  const isComposerEnabled = useIsIssueCommentComposerEnabled()

  return (
    <Stack direction="vertical" gap="normal">
      <CommentsViewControls
        threadPreviewsAuthorList={threadPreviewsAuthorList}
        issueCommentsAuthorList={issueCommentsAuthorList}
        threadPreviewFilterState={threadPreviewFilterState}
        setThreadPreviewFilterState={setThreadPreviewFilterState}
        issueCommentsFilterState={issueCommentsFilterState}
        setIssueCommentsFilterState={setIssueCommentsFilterState}
      />
      <Comments
        filteredThreadIds={filteredThreadIds}
        filteredIssueCommentIds={filteredIssueCommentIds}
        selectedReviewId={selectedReviewId}
        conversationItems={conversationItems}
        handleQuoteReply={handleQuoteReply}
        pathName={pathName}
        pullRequestAuthorLogin={pullRequestAuthorLogin}
        threadPreviews={threadPreviews}
        totalDiffCommentCount={totalDiffCommentCount}
      />
      {/* Collapsed rather than unmounted so an in-progress draft survives a switch to the review view. */}
      <IssueCommentComposer ref={composerRef} collapsed={!isComposerEnabled} />
    </Stack>
  )
}
